import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { pickLocale } from "../lib/format";
import { useI18n } from "../lib/i18n";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoMeta from "../components/storefront/SeoMeta";
import { TryOnModal } from "../components/atelier/TryOnModal";

/**
 * The AI fitting on its own route — a minimal, focused page rather than a modal
 * over the product. The whole process (upload → generate → result) lives here,
 * and the result leads back to buying the piece.
 *
 * The product is loaded from `?product=<handle>`, and the size the shopper had
 * already chosen travels in `?variant=<id>` so the fitting keeps their pick.
 */
export default function TryOnPage() {
  const { locale, t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const handle = params.get("product");
  const variantId = params.get("variant");
  const colourValueId = params.get("color");

  const [product, setProduct] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!handle) { setFailed(true); setLoading(false); return undefined; }
    let active = true;
    setLoading(true);
    Promise.allSettled([
      api.get(`/products/${handle}`),
      api.get("/tryon/config"),
    ]).then(([productResult, configResult]) => {
      if (!active) return;
      if (productResult.status === "fulfilled") {
        setProduct(productResult.value.data);
      } else {
        setFailed(true);
      }
      setConfig(
        configResult.status === "fulfilled"
          ? configResult.value.data
          : { enabled: false, provider_ready: false },
      );
      setLoading(false);
    });
    return () => { active = false; };
  }, [handle]);

  const selectedVariant = useMemo(() => {
    if (!product || !variantId) return null;
    return (product.variants || []).find((v) => v.id === variantId) || null;
  }, [product, variantId]);

  const title = product ? pickLocale(product.title, locale) : "";
  const backTo = handle ? `/products/${handle}` : "/shop";

  return (
    <div className="adf-tryon-route bg-[var(--adf-cream)] text-[var(--adf-ink)]">
      <SeoMeta
        title={`${t("tryon.modal_title", "AI geyinmə otağı")}${title ? ` — ${title}` : ""}`}
        locale={locale}
        canonicalPath="/try-on"
        noindex
      />
      <main id="main" data-screen-label="Try-on">
        {loading && (
          <div className="adf-container py-24 text-center text-[var(--adf-gray-2)]">
            {t("common.loading", "Yüklənir…")}
          </div>
        )}

        {!loading && failed && (
          <div className="adf-container py-24 text-center">
            <p className="mb-6 text-[var(--adf-gray-2)]">
              {t("tryon.product_missing", "Məhsul tapılmadı. Zəhmət olmasa mağazadan bir məhsul seçin.")}
            </p>
            <Link to="/shop" className="adf-cta adf-cta-dark inline-flex">
              {t("nav.shop", "Mağaza")}
            </Link>
          </div>
        )}

        {!loading && !failed && product && (
          <TryOnModal
            open
            mode="page"
            onClose={() => navigate(backTo)}
            productId={product.id}
            productHandle={product.handle}
            productTitle={title}
            product={product}
            selectedVariant={selectedVariant}
            variantId={variantId || undefined}
            colourValueId={colourValueId || undefined}
            runtimeConfig={config}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
